import { secureGetItem } from "../../data/utils";
import { getDoc, doc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../data/base";
import dayjs from "dayjs";

export const getUserProfile = async () => {
    try {
        const email = secureGetItem("email");
        if (!email) throw new Error("User email not found");

        const profileDoc = await getDoc(doc(db, "Profiles", email.toLowerCase()));
        if (!profileDoc.exists()) throw new Error("User profile not found");

        return profileDoc.data();
    } catch (error) {
        console.error("Error retrieving user profile:", error);
        throw error;
    }
};

export const getSubscriptions = async (clientRef, userRole, statusFilter = ["Active", "Renewed"]) => {
    try {

        let subscriptionsQuery;

        if (userRole === "Admin") {
            // Admin: all subscriptions with specified status
            subscriptionsQuery = query(
                collection(db, "ARShadesSubscriptions"),
                where("status", "in", statusFilter)
            );
        } else {
            // Other roles: filter by clientRef and specified status
            subscriptionsQuery = query(
                collection(db, "ARShadesSubscriptions"),
                where("clientRef", "==", clientRef),
                where("status", "in", statusFilter)
            );
        }

        const snapshot = await getDocs(subscriptionsQuery);

        const subscriptions = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        return subscriptions;
    } catch (error) {
        console.error("❌ Error retrieving subscriptions:", error);
        throw error;
    }
};

export const getCatalogueNames = async (catalogueIds) => {
    try {
        const catalogueNames = {};
        for (const id of catalogueIds) {
            const catalogueDoc = await getDoc(doc(db, "Catalogues", id));
            if (catalogueDoc.exists()) {
                catalogueNames[id] = catalogueDoc.data().name || id;
            } else {
                catalogueNames[id] = id; // Fallback to ID if not found
            }
        }
        return catalogueNames;
    } catch (error) {
        console.error("Error retrieving catalogue names:", error);
        return {};
    }
};

export const calculateSkuUsed = async (catalogueRefs) => {
    try {
        if (!catalogueRefs || catalogueRefs.length === 0) return 0;

        let totalSkuCount = 0;

        // Query CataloguesProducts for each catalogue
        for (const catalogRef of catalogueRefs) {
            const productsQuery = query(
                collection(db, "CataloguesProducts"),
                where("catalogRef", "==", catalogRef)
            );

            const productsSnapshot = await getDocs(productsQuery);

            // Sum up all list_variants lengths
            for (const productDoc of productsSnapshot.docs) {
                const productData = productDoc.data();
                const variants = productData.list_variants || [];
                totalSkuCount += variants.length;
            }
        }

        return totalSkuCount;

    } catch (error) {
        console.error('❌ Error calculating SKU used:', error);
        return 0;
    }
};

export const calculateGbUsed = async (subscription) => {
    try {
        if (!subscription || !subscription.list_catalogues?.list_catalogues_refs || subscription.list_catalogues.list_catalogues_refs.length === 0) {
            return 0;
        }

        // Step 1: Prendi il PRIMO catalogo dalla lista
        const firstCatalogueId = subscription.list_catalogues.list_catalogues_refs[0];

        // Step 2: Prendi i dati del catalogo
        const catalogueDoc = await getDoc(doc(db, "Catalogues", firstCatalogueId));
        if (!catalogueDoc.exists()) {
            return 0;
        }

        const catalogueData = catalogueDoc.data();
        if (!catalogueData.dataConsumption) {
            return 0;
        }

        // Step 3: Prendi il contatore
        const counterDoc = await getDoc(doc(db, "Contatori_Brand", catalogueData.dataConsumption));
        if (!counterDoc.exists()) {
            return 0;
        }

        const counterData = counterDoc.data();

        // Step 4: Filtra per periodo e somma tutti i bandwidth_month
        let startDate, endDate;

        // Handle different date formats for startDate
        if (typeof subscription.startDate === 'string') {
            startDate = dayjs(subscription.startDate);
        } else if (subscription.startDate.seconds) {
            startDate = dayjs.unix(subscription.startDate.seconds);
        } else {
            startDate = dayjs(subscription.startDate);
        }

        // Handle different date formats for endDate
        if (typeof subscription.endDate === 'string') {
            endDate = dayjs(subscription.endDate);
        } else if (subscription.endDate.seconds) {
            endDate = dayjs.unix(subscription.endDate.seconds);
        } else {
            endDate = dayjs(subscription.endDate);
        }

        let totalBandwidthMB = 0;

        // Processa tutti gli array di bandwidth
        ['bandwidth_3dviewer', 'bandwidth_array', 'bandwidth_webVto'].forEach(serviceKey => {
            if (counterData[serviceKey] && Array.isArray(counterData[serviceKey])) {
                counterData[serviceKey].forEach(item => {
                    const itemDate = dayjs(`${item.year}-${String(item.month).padStart(2, '0')}-01`);
                    if (itemDate.isBetween(startDate, endDate, 'month', '[]')) {
                        totalBandwidthMB += item.bandwidth_month || 0;
                    }
                });
            }
        });

        // Converti da MB a GB
        return parseFloat((totalBandwidthMB / 1024).toFixed(2));

    } catch (error) {
        console.error('Error calculating GB used:', error);
        return 0;
    }
};

// Function to get main domain from mainCatalogRef
export const getMainDomain = async (subscription, setMainDomain) => {
    if (!subscription?.mainCatalogRef) {
        setMainDomain(null);
        return;
    }

    try {
        const catalogueDoc = await getDoc(doc(db, "Catalogues", subscription.mainCatalogRef));
        if (catalogueDoc.exists()) {
            const catalogueData = catalogueDoc.data();
            setMainDomain(catalogueData.webDomain || null);
        } else {
            setMainDomain(null);
        }
    } catch (error) {
        console.error(`Error fetching main catalog domain ${subscription.mainCatalogRef}:`, error);
        setMainDomain(null);
    }
};

// Function to get catalogue data
export const getCatalogueData = async (catalogueId) => {
    try {
        const catalogueDoc = await getDoc(doc(db, "Catalogues", catalogueId));
        if (catalogueDoc.exists()) {
            return catalogueDoc.data();
        }
        return null;
    } catch (error) {
        console.error(`Error fetching catalogue ${catalogueId}:`, error);
        return null;
    }
};

// Function to get counter data
export const getCounterData = async (dataConsumptionRef) => {
    try {
        const counterDoc = await getDoc(doc(db, "Contatori_Brand", dataConsumptionRef));
        if (counterDoc.exists()) {
            return counterDoc.data();
        }
        return null;
    } catch (error) {
        console.error(`Error fetching counter ${dataConsumptionRef}:`, error);
        return null;
    }
};

// Function to check if there are historical subscriptions available
export const checkHistoryData = async (subscription, setHasHistoryData, setCheckingHistory) => {
    if (!subscription) {
        setHasHistoryData(false);
        return;
    }

    setCheckingHistory(true);
    try {
        // Prendi il docRef della subscription selezionata
        const mainSubscriptionDocRef = subscription.id;

        // Cerca tutte le subscription che hanno questo docRef nel campo mainSubscriptionRef
        const historyQuery = query(
            collection(db, "ARShadesSubscriptions"),
            where("mainSubscriptionRef", "==", mainSubscriptionDocRef)
        );

        const historySnapshot = await getDocs(historyQuery);

        // Se ci sono subscription collegate, allora ci sono dati storici
        const hasHistoricalData = historySnapshot.docs.length > 0;

        setHasHistoryData(hasHistoricalData);
    } catch (error) {
        console.error('Error checking history data:', error);
        setHasHistoryData(false);
    } finally {
        setCheckingHistory(false);
    }
};